import React from 'react';
import { useIntl } from 'react-intl';
import {
  ApiFilterOperation,
  EmptyComponent,
  TableCells,
  TableColumn,
  TableColumnAlign,
  eqFilterParams,
} from '@eas/common-web';
import { useCustomFilterAutocomplete } from '@composite/autocomplete-item/autocomplete-item-with-deactivated-items';
import { Redirect } from '@composite/redirect/redirect';
import {
  autocompleteColumnMapper,
  multipleAutocompleteColumnMapper,
} from '@components/form/misc/label-mappers';
import { CourtFile } from '@models';
import { EvidenceBrowserUrl, Role } from '@enums';
import { useJudges } from '../../judge/judge-api';
import { useUserRoles } from '../../role/role-api';
import { useCourtFileColumns } from '../court-file-columns';

export function useColumns(): TableColumn<CourtFile>[] {
  const intl = useIntl();
  const {
    columnJid,
    columnDeliveryDate,
    columnOpponent,
    columnProposers,
    columnType,
  } = useCourtFileColumns();
  const useSecretaries = () =>
    useUserRoles({
      ...eqFilterParams({ field: 'role.id', value: Role.SECRETARY }),
      include: ['DEACTIVATED', 'DELETED'],
    });
  const useAssistants = () =>
    useUserRoles({
      ...eqFilterParams({ field: 'role.id', value: Role.ASSISTANT }),
      include: ['DEACTIVATED', 'DELETED'],
    });

  const useIndexes = () =>
    useUserRoles({
      ...eqFilterParams({ field: 'role.id', value: Role.INDEX }),
      include: ['DEACTIVATED'],
    });

  return [
    {
      datakey: 'id',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_JUDGE_OFFICE__TABLE__COLUMN__REDIRECT',
        defaultMessage: 'Přesměrování',
      }),
      NameComponent: EmptyComponent,
      width: 50,
      CellComponent: function Cell(props) {
        return (
          <>
            <Redirect
              url={EvidenceBrowserUrl.COURT_FILES_JUDGE_OFFICE}
              id={props.value}
              showRedirectInCurrent={false}
            />
          </>
        );
      },
      align: TableColumnAlign.LEFT,
    },
    columnDeliveryDate,
    {
      datakey: 'number.value',
      sortkey: 'number.valueSort',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_JUDGE_OFFICE__TABLE__COLUMN__FILE_NUMBER',
        defaultMessage: 'Spisová značka',
      }),
      width: 120,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    columnJid,
    {
      datakey: 'judge',
      filterkey: 'judge.id',
      sortkey: 'judge.name',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_JUDGE_OFFICE__TABLE__COLUMN__JUDGE',
        defaultMessage: 'Soudce zpravodaj',
      }),
      width: 200,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: () => useJudges({ include: ['DEACTIVATED'] }),
      }),
      filterOperation: ApiFilterOperation.EQ,
      filterDialogSpan: 2,
      sortable: true,
      filterable: true,
    },
    { ...columnProposers, filterDialogSpan: 2 },
    { ...columnOpponent, filterDialogSpan: 2 },
    columnType,
    {
      datakey: 'secretaryUserRole',
      filterkey: 'secretaryUserRole.id',
      sortkey: 'secretaryUserRole.name',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_JUDGE_OFFICE__TABLE__COLUMN__SECRETARY',
        defaultMessage: 'Tajemnice',
      }),
      width: 200,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: useSecretaries,
      }),
      filterOperation: ApiFilterOperation.EQ,
      filterDialogSpan: 2,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'assistantUserRoles',
      filterkey: 'assistantUserRoles.id',
      sortkey: 'assistantUserRoles.flat',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_JUDGE_OFFICE__TABLE__COLUMN__ASSISTANTS',
        defaultMessage: 'Asistent',
      }),
      width: 200,
      valueMapper: multipleAutocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: useAssistants,
      }),
      filterOperation: ApiFilterOperation.EQ,
      filterDialogSpan: 2,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'indexUserRole',
      filterkey: 'indexUserRole.id',
      sortkey: 'indexUserRole.name',
      name: intl.formatMessage({
        id: 'ES__COURT_FILES_JUDGE_OFFICE__TABLE__COLUMN__INDEX',
        defaultMessage: 'Vedoucí rejstříku',
      }),
      width: 200,
      valueMapper: autocompleteColumnMapper,
      CellComponent: TableCells.TextCell,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: useIndexes,
      }),
      filterOperation: ApiFilterOperation.EQ,
      filterDialogSpan: 2,
      sortable: true,
      filterable: true,
    },
  ];
}
